import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/admin/login';

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const { location } = history;

  // 公开访问路径，无需获取用户信息。
  // 监控平台需要登录后的俱乐部 Logo 和标题，因此不能放入公开白名单。
  const publicPaths = ['/race/view/'];
  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

  // 登录页或公开路径，不获取用户信息
  if (location.pathname === loginPath || isPublicPath) {
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 其他页面需要登录
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// 根据当前用户动态获取标题和 Logo
const getDynamicSettings = (currentUser?: API.CurrentUser) => {
  const isClubAdmin = currentUser?.role === 'club_admin';
  const hasClub = !!currentUser?.club;

  // 俱乐部管理员登录且有关联俱乐部时，使用俱乐部名称和 Logo
  if (isClubAdmin && hasClub) {
    return {
      title: currentUser.club?.name || '圣巴云赛事救援管理系统',
      logo: currentUser.club?.logo_url || 'https://mcro.oss-cn-shanghai.aliyuncs.com/seba.jpg',
    };
  }

  // 默认返回系统名称和 Logo
  return {
    title: '圣巴云赛事救援管理系统',
    logo: 'https://mcro.oss-cn-shanghai.aliyuncs.com/seba.jpg',
  };
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const dynamicSettings = getDynamicSettings(initialState?.currentUser);

  return {
    // 先展开默认 settings，确保其他配置生效
    ...initialState?.settings,
    // 动态设置标题和 Logo，俱乐部管理员显示俱乐部信息（覆盖默认值）
    title: dynamicSettings.title,
    logo: dynamicSettings.logo,
    actionsRender: () => [
      <Question key="doc" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 公开访问路径，无需登录。
      // 监控平台依赖登录后的动态品牌信息，因此未登录时仍应进入登录页。
      const publicPaths = ['/race/view/'];
      const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

      // 如果没有登录且不是公开路径，重定向到管理员登录页面
      if (!initialState?.currentUser && location.pathname !== loginPath && !isPublicPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>OpenAPI 文档</span>
        </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 *
 * 生产环境部署说明：
 * 域名：https://trek.sebayun.com
 * 前端：/
 * 后端：/api/（Nginx 自动转发）
 */
export const request: RequestConfig = {
  // baseURL 使用空字符串
  // 请求 /admin/auth/login 会被 Nginx 转发到 /api/admin/auth/login
  baseURL: '',
  ...errorConfig,
};
